import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ClinicalDataLanguageNotice } from '@/components/ClinicalDataLanguageNotice';
import ExplainButton from './ExplainButton';
import { formatRecordDate, inferMedicationGroup, isActiveMedication, slugify, type MedicationGroup } from './records-utils';

interface Medication {
  id: string;
  name: string;
  dosage?: string;
  status?: string;
  prescribedDate?: string;
}

interface MedicationsSectionProps {
  medications: Medication[];
  /** Plain-language names and groups from the startup payload, matched to the record by name. */
  items?: Array<{ name: string; plainName: string; group: MedicationGroup }>;
  patientFirstName: string;
  onAskQuestion: (question: string) => void;
  variant: 'main' | 'rail';
}

const GROUP_ORDER: MedicationGroup[] = ['scheduled', 'asNeeded', 'continuous'];
const GROUP_LABEL_KEYS: Record<MedicationGroup, string> = {
  scheduled: 'records.groupScheduled',
  asNeeded: 'records.groupAsNeeded',
  continuous: 'records.groupContinuous',
};

export default function MedicationsSection({ medications, items, patientFirstName, onAskQuestion, variant }: MedicationsSectionProps) {
  const { t, i18n } = useTranslation();
  const [showStopped, setShowStopped] = useState(false);
  const rowPadding = variant === 'rail' ? 'px-3' : 'px-4';

  if (!medications || medications.length === 0) {
    return <p className="text-sm text-gray-500 text-center py-4">{t('dashboard.noMedicationsData')}</p>;
  }

  const byName = new Map((items || []).map((item) => [item.name.toLowerCase(), item]));
  const active = medications.filter((med) => isActiveMedication(med.status));
  const stopped = medications.filter((med) => !isActiveMedication(med.status));

  const grouped: Record<MedicationGroup, Medication[]> = { scheduled: [], asNeeded: [], continuous: [] };
  active.forEach((med) => {
    const group = byName.get(med.name.toLowerCase())?.group ?? inferMedicationGroup(med);
    grouped[group].push(med);
  });

  const renderRow = (med: Medication) => {
    const plainName = byName.get(med.name.toLowerCase())?.plainName;
    const showClinicalName = !!plainName && plainName.toLowerCase() !== med.name.toLowerCase();
    const started = formatRecordDate(med.prescribedDate, i18n.resolvedLanguage || 'en');
    return (
      <div key={med.id} className={`flex items-start justify-between gap-3 ${rowPadding} py-2.5`} data-testid={`row-medication-${med.id}`}>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-text-primary">
            <span>{plainName || med.name}</span>
            {showClinicalName && <span className="font-normal text-text-secondary"> ({med.name})</span>}
          </p>
          {med.dosage && <p className="text-sm text-text-secondary mt-0.5">{med.dosage}</p>}
          {started && <p className="text-sm text-text-secondary mt-0.5">{t('records.startedOn', { date: started })}</p>}
        </div>
        <ExplainButton
          onClick={() => onAskQuestion(t('explain.medicationDetail', { med: med.name, name: patientFirstName }))}
          ariaLabel={t('explain.medicationAriaLabel', { name: med.name })}
          testId={`button-explain-${slugify(med.name)}`}
        />
      </div>
    );
  };

  return (
    <div>
      <ClinicalDataLanguageNotice />
      {GROUP_ORDER.filter((group) => grouped[group].length > 0).map((group) => (
        <div key={group} className="mb-4" data-testid={`medications-group-${group}`}>
          <h3 className="text-sm font-semibold text-text-primary mb-2">{t(GROUP_LABEL_KEYS[group])}</h3>
          <div className="rounded-lg border bg-white divide-y">{grouped[group].map(renderRow)}</div>
        </div>
      ))}
      {stopped.length > 0 && (
        <div>
          <button
            onClick={() => setShowStopped(!showStopped)}
            className="flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary"
            aria-expanded={showStopped}
            data-testid="button-toggle-stopped-medications"
          >
            {showStopped ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            {t('records.stoppedMedications', { count: stopped.length })}
          </button>
          {showStopped && <div className="rounded-lg border bg-white divide-y mt-2">{stopped.map(renderRow)}</div>}
        </div>
      )}
    </div>
  );
}
